import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { getCustomSelectStyles } from "../../../../shared";
import type { Device, ThemeClasses } from "../types";
import {
  fetchTransferPreview,
  type TransferBackup,
  type TransferPreview,
  type TransferStatusPolicy,
} from "../../../../../../features/devices/mutations";

const ALL_SOURCES = ["positions", "events", "commands", "geofences"] as const;

const STATUS_POLICIES = ["keep", "active", "inactive"] as const;

interface CompanyOption {
  id: string;
  name: string;
  origin?: string[] | null;
}

interface DeviceTransferModalProps {
  device: Device;
  companies: CompanyOption[];
  currentCompanyId: string | null;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  onClose: () => void;
  onConfirm: (
    toCompanyId: string,
    backup: TransferBackup,
    statusPolicy: TransferStatusPolicy,
  ) => void;
  isPending?: boolean;
  t: (key: string, options?: any) => string;
}

const inSg = (c?: CompanyOption | null) =>
  !!c && Array.isArray(c.origin) && c.origin.includes("v5");

export function DeviceTransferModal({
  device,
  companies,
  currentCompanyId,
  isDarkMode,
  themeClasses,
  onClose,
  onConfirm,
  isPending = false,
  t,
}: DeviceTransferModalProps) {
  const [targetId, setTargetId] = useState<string | null>(null);
  const [statusPolicy, setStatusPolicy] = useState<TransferStatusPolicy>("keep");
  const [preview, setPreview] = useState<TransferPreview | null>(null);
  const [pendingBackup, setPendingBackup] = useState<TransferBackup | null>(null);
  const [sel, setSel] = useState<Record<string, boolean>>({
    positions: true,
    events: true,
    commands: true,
    geofences: true,
  });

  useEffect(() => {
    let alive = true;
    fetchTransferPreview(device.id)
      .then((data) => alive && setPreview(data))
      .catch(() => alive && setPreview(null));
    return () => {
      alive = false;
    };
  }, [device.id]);

  const options = useMemo(
    () =>
      companies
        .filter((c) => c.id !== currentCompanyId)
        .map((c) => ({ value: c.id, label: c.name })),
    [companies, currentCompanyId],
  );

  const current = companies.find((c) => c.id === currentCompanyId) || null;
  const target = companies.find((c) => c.id === targetId) || null;

  const noticeKey = (() => {
    if (!target) return null;
    const a = inSg(current);
    const b = inSg(target);
    if (a && b) return "devices.transfer.notice.both";
    if (!a && !b) return "devices.transfer.notice.v5Only";
    if (a && !b) return "devices.transfer.notice.leavesSg";
    return "devices.transfer.notice.entersSg";
  })();

  const targetInSg = inSg(target);
  const currentInSg = preview ? preview.companyInSg : inSg(current);
  const canTransfer = !!targetId;

  const keepRisksSgWin = statusPolicy === "keep" && currentInSg && targetInSg;
  const applyWarning = keepRisksSgWin || (statusPolicy !== "keep" && !targetInSg);

  const applyMessage = (() => {
    if (statusPolicy === "keep") {
      return keepRisksSgWin
        ? t("devices.transfer.status.apply.keepSgWins")
        : t("devices.transfer.status.apply.keep");
    }
    if (!target) return t("devices.transfer.status.apply.pickTarget");
    if (targetInSg) {
      return currentInSg
        ? t("devices.transfer.status.apply.both")
        : t("devices.transfer.status.apply.entersSg");
    }
    return currentInSg
      ? t("devices.transfer.status.apply.leavesSg")
      : t("devices.transfer.status.apply.v5Only");
  })();

  const selectedSources = ALL_SOURCES.filter((s) => sel[s]);
  const muted = isDarkMode ? "text-gray-300" : "text-gray-500";
  const infoBox = isDarkMode
    ? "border-sky-500/30 bg-sky-500/10 text-sky-200"
    : "border-sky-300 bg-sky-50 text-sky-800";
  const warnBox = isDarkMode
    ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
    : "border-amber-300 bg-amber-50 text-amber-800";
  const dangerBox = isDarkMode
    ? "border-red-500/30 bg-red-500/10 text-red-200"
    : "border-red-300 bg-red-50 text-red-800";

  const drag = preview?.drag;
  const sharedRemolques = drag?.sharedRemolques || [];

  const confirming = pendingBackup !== null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      onClick={isPending ? undefined : onClose}
    >
      <div
        className={`max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border p-6 shadow-xl ${themeClasses.cardBg} ${themeClasses.cardBorder}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={`text-lg font-bold ${themeClasses.text}`}>
          {confirming
            ? t("devices.transfer.confirm.title")
            : t("devices.transfer.title")}
        </h2>
        <p className={`mt-1 text-sm ${muted}`}>
          {t("devices.transfer.subtitle", {
            name: device.name || device.identifier,
          })}
        </p>

        {confirming ? (
          <div className="mt-4 space-y-3">
            <div className={`rounded-lg border px-3 py-2 text-sm ${infoBox}`}>
              <p>
                <strong>{t("devices.transfer.confirm.target")}:</strong>{" "}
                {target?.name}
              </p>
              <p>
                <strong>{t("devices.transfer.confirm.status")}:</strong>{" "}
                {t(`devices.transfer.status.options.${statusPolicy}`)}
              </p>
              <p>
                <strong>{t("devices.transfer.confirm.backup")}:</strong>{" "}
                {pendingBackup?.mode === "archive"
                  ? pendingBackup.sources
                      .map((s) => t(`devices.transfer.sources.${s}`))
                      .join(", ")
                  : t("devices.transfer.confirm.noBackup")}
              </p>
            </div>

            <div className={`rounded-lg border px-3 py-2 text-xs ${dangerBox}`}>
              {t("devices.transfer.confirm.purgeWarning")}
            </div>

            {(drag?.vehicle ||
              (drag?.remolques?.length || 0) > 0 ||
              (drag?.drivers?.length || 0) > 0) && (
              <div className={`rounded-lg border px-3 py-2 text-xs ${warnBox}`}>
                <p className="font-medium">
                  {t("devices.transfer.confirm.dragTitle")}
                </p>
                {drag?.vehicle && (
                  <p>
                    {t("devices.transfer.confirm.vehicle")}: {drag.vehicle.name}
                  </p>
                )}
                {(drag?.remolques?.length || 0) > 0 && (
                  <p>
                    {t("devices.transfer.confirm.remolques")}:{" "}
                    {drag!.remolques.map((r) => r.name || r.id).join(", ")}
                  </p>
                )}
                {(drag?.drivers?.length || 0) > 0 && (
                  <p>
                    {t("devices.transfer.confirm.driversUnassigned")}:{" "}
                    {drag!.drivers.map((d) => d.name || d.id).join(", ")}
                  </p>
                )}
              </div>
            )}

            {sharedRemolques.length > 0 && (
              <div className={`rounded-lg border px-3 py-2 text-xs ${dangerBox}`}>
                {t("devices.transfer.confirm.sharedRemolque", {
                  names: sharedRemolques
                    .map((r) => r.name || r.id)
                    .join(", "),
                })}
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setPendingBackup(null)}
                disabled={isPending}
                className={`rounded-lg px-3 py-2 text-sm font-medium ${
                  isDarkMode
                    ? "text-gray-300 hover:bg-white/5"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {t("devices.transfer.confirm.back")}
              </button>
              <button
                type="button"
                disabled={isPending}
                onClick={() =>
                  targetId && onConfirm(targetId, pendingBackup!, statusPolicy)
                }
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isPending
                  ? t("devices.transfer.processing")
                  : t("devices.transfer.confirm.accept")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-4">
              <label
                className={`block text-sm font-medium mb-1 ${themeClasses.text}`}
              >
                {t("devices.transfer.targetLabel")}
              </label>
              <Select
                options={options}
                styles={getCustomSelectStyles(isDarkMode, false)}
                placeholder={t("devices.transfer.targetPlaceholder")}
                value={options.find((o) => o.value === targetId) || null}
                onChange={(opt: any) => setTargetId(opt ? opt.value : null)}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            {noticeKey && (
              <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${warnBox}`}>
                {t(noticeKey)}
              </div>
            )}

            <div className="mt-4">
              <p className={`text-sm font-medium ${themeClasses.text}`}>
                {t("devices.transfer.status.label")}
              </p>
              <p className={`text-xs mb-2 ${muted}`}>
                {t("devices.transfer.status.hint")}
              </p>

              <div className="grid grid-cols-3 gap-2">
                {STATUS_POLICIES.map((p) => (
                  <label
                    key={p}
                    className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-center ${
                      statusPolicy === p
                        ? "border-primary-500 bg-primary-500/10"
                        : isDarkMode
                          ? "border-dark-border hover:bg-white/5"
                          : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="transfer-status-policy"
                      className="h-4 w-4 shrink-0 accent-primary-500"
                      checked={statusPolicy === p}
                      onChange={() => setStatusPolicy(p)}
                    />
                    <span className={`text-sm ${themeClasses.text}`}>
                      {t(`devices.transfer.status.options.${p}`)}
                    </span>
                  </label>
                ))}
              </div>

              {applyMessage && (
                <div
                  className={`mt-2 rounded-lg border px-3 py-2 text-xs ${
                    applyWarning ? warnBox : infoBox
                  }`}
                >
                  {applyMessage}
                </div>
              )}
            </div>

            <div className="mt-4">
              <p className={`text-sm font-medium ${themeClasses.text}`}>
                {t("devices.transfer.backupLabel")}
              </p>
              <p className={`text-xs mb-2 ${muted}`}>
                {t("devices.transfer.backupHint")}
              </p>
              <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                {ALL_SOURCES.map((s) => (
                  <label
                    key={s}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 ${
                      isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary-500"
                      checked={sel[s]}
                      onChange={(e) =>
                        setSel((p) => ({ ...p, [s]: e.target.checked }))
                      }
                    />
                    <span className={`text-sm ${themeClasses.text}`}>
                      {t(`devices.transfer.sources.${s}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-nowrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium ${
                  isDarkMode
                    ? "text-gray-300 hover:bg-white/5"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {t("devices.transfer.cancel")}
              </button>
              <button
                type="button"
                disabled={!canTransfer}
                onClick={() => setPendingBackup({ mode: "purge" })}
                className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium border ${
                  isDarkMode
                    ? "border-dark-border hover:bg-white/5 text-gray-200"
                    : "border-gray-200 hover:bg-gray-50 text-gray-700"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {t("devices.transfer.confirmNoBackup")}
              </button>
              <button
                type="button"
                disabled={!canTransfer || selectedSources.length === 0}
                onClick={() =>
                  setPendingBackup({
                    mode: "archive",
                    sources: selectedSources,
                  })
                }
                className="whitespace-nowrap rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("devices.transfer.confirmBackup")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
